// src/services/referralScheduleService.js
//
// Client data layer for the Referral appointment-schedule PDF import. Mirrors
// protocolExtractionService.js: an authenticated POST (Firebase ID token in the
// Authorization header) to a Cloud Function, scoped to the active tenant/site.
//
// The heavy lifting (render pages, vision-extract, validate/normalize) runs
// server-side in the extractReferralSchedulePdf Cloud Function. This file just
// turns the selected PDF into base64 and returns the reviewed rows.

import { auth } from "../firebase";
import { getActiveTenantId, getActiveSiteId } from "./activeTenant";
import { FUNCTIONS_BASE } from "../config/functionsBase";

// Authenticated POST to a Cloud Function (sends the Firebase ID token so the
// server verifies the caller AND their site membership before doing anything).
const callFn = async (name, body) => {
  const user = auth.currentUser;
  if (!user) throw new Error("You must be signed in.");
  const idToken = await user.getIdToken();
  const resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify(body || {}),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(data.error || `${name} failed (${resp.status}).`);
  }
  return data;
};

// Read a File into base64 (without the "data:...;base64," prefix).
const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read the PDF file."));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });

const pageList = (v) =>
  Array.isArray(v) ? v.map(Number).filter((n) => Number.isInteger(n) && n > 0) : [];

/**
 * Upload a schedule PDF for server-side extraction and return the rows plus a
 * page-level read report, so pages the reader couldn't handle are surfaced
 * instead of silently dropping their patients.
 *
 * rows: canonical shape from functions/referralSchedule.js (patientName,
 * firstName, mrn, appointmentDate, appointmentTime, locationCode, locationName,
 * mobilePhone, sendable, sourcePage, confidence, warnings).
 *
 * @param {File} file a PDF File
 * @param {{ locationMap?: Object }} [opts] optional extra location-code -> name map
 * @returns {Promise<{rows: Object[], pageCount: number, failedPages: number[],
 *   emptyPages: number[], skippedPages: number, maxPages: number}>}
 */
export const extractReferralScheduleReport = async (file, opts = {}) => {
  if (!file) throw new Error("No file selected.");
  const isPdf =
    file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
  if (!isPdf) throw new Error("Please select a PDF file.");

  const pdfBase64 = await fileToBase64(file);
  const data = await callFn("extractReferralSchedulePdf", {
    pdfBase64,
    tenantId: getActiveTenantId(),
    siteId: getActiveSiteId(),
    locationMap: opts.locationMap || undefined,
  });
  return {
    rows: Array.isArray(data.rows) ? data.rows : [],
    pageCount: Number(data.pageCount) || 0,
    // Older deployed functions don't send these; absent means "no issues known".
    failedPages: pageList(data.failedPages),
    emptyPages: pageList(data.emptyPages),
    skippedPages: Math.max(0, Number(data.skippedPages) || 0),
    maxPages: Number(data.maxPages) || 0,
  };
};

/**
 * Upload a schedule PDF and return just the extracted rows.
 * @returns {Promise<Array<Object>>}
 */
export const extractReferralSchedulePdf = async (file, opts = {}) =>
  (await extractReferralScheduleReport(file, opts)).rows;
